import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Plus, Search, Loader2, ChevronRight, FolderOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { EmployeeFormDialog, CONTRACT_LABELS } from "../components/employees/EmployeeFormDialog";
import { EmployeesTabs } from "../components/employees/EmployeesTabs";
import { getEmployees } from "../services/api";

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getEmployees();
      setEmployees(res.data);
    } catch {
      toast.error("Erreur de chargement des employés");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = employees.filter((e) =>
    `${e.first_name} ${e.last_name} ${e.position || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="employees-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase size={24} className="text-[#26788A]" /> Employés
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Fiches, bulletins de salaire, documents, contrats et planning</p>
        </div>
        <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={() => setDialogOpen(true)} data-testid="new-employee-btn">
          <Plus size={16} className="mr-1" /> Nouvel employé
        </Button>
      </div>

      <EmployeesTabs />

      <div className="relative max-w-xs mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Rechercher..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="employee-search-input" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl" data-testid="employees-empty-state">
          <Briefcase size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{employees.length === 0 ? "Aucun employé pour le moment" : "Aucun résultat"}</p>
          {employees.length === 0 && (
            <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus size={15} className="mr-1" /> Ajouter le premier employé
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((emp) => (
            <button
              key={emp.id}
              className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:shadow-md hover:border-teal-200 transition-all text-left"
              onClick={() => navigate(`/employes/${emp.id}`)}
              data-testid={`employee-card-${emp.id}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#26788A] text-white flex items-center justify-center font-bold text-sm shrink-0">
                {(emp.first_name?.[0] || "") + (emp.last_name?.[0] || "")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{emp.first_name} {emp.last_name}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{CONTRACT_LABELS[emp.contract_type] || emp.contract_type}</span>
                  {emp.is_active === false && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Inactif</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {emp.position || "Poste non renseigné"}
                  {emp.hire_date && <> · depuis le {new Date(emp.hire_date + "T00:00:00").toLocaleDateString("fr-FR")}</>}
                </p>
              </div>
              <span className="hidden sm:flex items-center gap-1 text-xs text-slate-400"><FolderOpen size={13} /> {emp.files_count || 0}</span>
              <ChevronRight size={16} className="text-slate-300" />
            </button>
          ))}
        </div>
      )}

      <EmployeeFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} employee={null} onSaved={load} />
    </div>
  );
}
